'use client';

import vtRequestId from '@nrdy-marketing-engine/api-fetch/src/vt-request-id';
import { EventEmitterContext } from '@nrdy-marketing-engine/eventing';
import FormContext from '@nrdy-marketing-engine/form-context/src/form-context';
import { track } from '@nrdy-marketing-engine/tracking';
import { isEmpty } from '@s-libs/micro-dash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProps } from '../@types/form-props';
import { PanelData } from '../@types/panel-data';
import { getBranches, getMaxRoute } from './branches/branches';
import useFormValidation from './hooks/use-form-validation';
import useLoadingState from './hooks/use-loading-state';
import useStack from './hooks/use-stack';
import moveForward from './move-forward';
import Panel, { isPanel } from './panel';
import { isFormInputPanel } from './panel/form-input-panel';
import transformSteps from './transform-steps';
import validate from './validate';

/**
 * Performs any setup of the form, extracted out of the `useEffect` block.
 */

function formUseEffect(initialData, steps, resetPanels, setFormData) {
  setFormData(initialData || {});
  resetPanels([steps.find(isPanel)]);
}

/**
 * Tracks form wizard panel events such as load, start or completion.
 */
function trackPanelEvent(currentStepName, formMetaData, eventType: 'load' | 'complete' | 'start') {
  let trackLabel: string;
  let trackAction: string;

  if (eventType === 'start') {
    trackLabel = `formWizard_${eventType}`;
    trackAction = `formWizard_${eventType}`;
  } else {
    trackLabel = `formWizard_${currentStepName}_${eventType}`;
    trackAction = `formWizard.${currentStepName}.${eventType}`;
  }

  track('lead-form', {
    category: 'form_wizard_panel',
    label: trackLabel,
    target: trackLabel,
    dataAttributes: { ...formMetaData, leadFormAction: trackAction },
  });
}

/**
 * A component that handles rendering a multi-step form, typically served from NSP.
 */
function Form({ initialData, steps, showLeadImage = false }: FormProps) {
  const [formData, setFormData] = useState({});
  const formSteps = useMemo(() => transformSteps(steps), [steps]);
  const eventEmitter = useContext(EventEmitterContext);
  const formContextValue = useContext(FormContext);
  const totalBranches = useMemo(() => getBranches(formSteps), [formSteps]);
  const getMaxRouteCallback = useCallback(
    (formStack) => getMaxRoute(totalBranches, formStack),
    [totalBranches]
  );

  // Track the validation state of the form data.
  const [validationErrors, withFormValidation] = useFormValidation();
  const [loading, withLoadingState] = useLoadingState();

  // Track a stack of panels which represents the navigation state of the form,
  // pushing an item navigates forward, popping an item navigates back.
  const {
    currentTopItem: currentPanel,
    push: pushPanel,
    pop: popPanel,
    popN: popNPanels,
    peekBehind: peekBehindPanel,
    reset: resetPanels,
    currentStep: currentStepIndex,
    stack: formStack,
  } = useStack<PanelData>([formSteps.find(isPanel)] as PanelData[]);

  // Calculate the maximum number of steps in the form.
  const maxSteps = getMaxRouteCallback(formStack);

  // If the form data or steps change for any reason, we should clear the state
  // and reset/rebuild the form.
  useEffect(() => {
    formUseEffect(initialData, formSteps, resetPanels, setFormData);
  }, [initialData, formSteps, resetPanels, setFormData]);

  useEffect(() => {
    trackPanelEvent(currentPanel?.stepName, getFormMetadata(), 'load');
  }, [currentPanel]);

  /** Handles any action that triggers moving back in the form. */
  async function handleBack() {
    await withFormValidation(async () => {
      const behindPanel = peekBehindPanel();
      let newTopPanel;

      // we have to skip this panel when navigating backwards
      if (behindPanel?._modelApiKey === 'panel_interstitial') newTopPanel = popNPanels(2);
      else newTopPanel = popPanel();

      // we removed the last panel, so go back
      if (!newTopPanel) eventEmitter.emit('CLOSE_FORM');
    });
  }

  async function trackValidationErrors(validationErrors, formData, currentPanel) {
    const trackingObject = await constructTrackingObject(validationErrors, formData, currentPanel);
    if (!trackingObject) return;
    track('validation_error', trackingObject);
  }

  async function constructTrackingObject(validationErrors, formData, currentPanel) {
    const { inputFields } = currentPanel;

    if (!validationErrors || !inputFields) return;

    const attributes = Object.fromEntries(
      inputFields.map(({ name, type }) => [name || type, formData[name || type]])
    );

    const errorsArrayString = (Object.values(validationErrors)[0] as string[]).join(', ');

    return {
      category: 'inputValidationError',
      label: currentPanel.stepName,
      newrelicMetadata: {},
      request_id: vtRequestId(),
      target: Object.values(attributes)[0] || ' ',
      text: errorsArrayString,
    };
  }

  function cleanInput(input: string): string | null {
    return input === '' ? null : input;
  }

  async function cleanFormData(formData, currentPanel) {
    const { inputFields } = currentPanel;
    if (!inputFields) return formData;

    const nameField = inputFields.find((field) => field.name === 'full_name');

    if (nameField) {
      const nameValue = cleanInput(formData[nameField.name || nameField.type]);

      if (nameValue !== null && typeof nameValue !== 'undefined') {
        const singleSpacedOutput = nameValue.trim().replace(/\s+/g, ' ');
        return { ...formData, [nameField.name]: singleSpacedOutput };
      }
    }

    return formData;
  }

  function getFormMetadata() {
    return {
      leadFormName: formContextValue.slug || formContextValue.leadFormName,
    };
  }

  /** Handles any action that triggers moving forward in the form. */
  async function handleNext() {
    await withFormValidation(async () => {
      await withLoadingState(async () => {
        let currentFormData = formData;

        if (isFormInputPanel(currentPanel)) {
          currentFormData = await cleanFormData(formData, currentPanel);
          setFormData(currentFormData);
        }

        try {
          await validate(currentFormData, currentPanel);
        } catch (e) {
          trackValidationErrors(e, currentFormData, currentPanel);
          throw e;
        }

        if (isFormInputPanel(currentPanel)) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        const formMetaData = getFormMetadata();

        const [nextPanel, newFormData] = await moveForward(
          currentPanel,
          formSteps,
          currentFormData,
          formMetaData
        );

        setFormData(newFormData);
        if (nextPanel) pushPanel(nextPanel);
        if (currentStepIndex === 1) {
          trackPanelEvent(currentPanel.stepName, formMetaData, 'start');
        }
        trackPanelEvent(currentPanel.stepName, formMetaData, 'complete');
      });
    });
  }

  /** Callback function from each panel to handle entering input values. */
  async function handleInput(newData: { [key: string]: any }) {
    setFormData((formData) => ({ ...formData, ...newData }));

    if (!isEmpty(validationErrors)) {
      await withFormValidation(async () => {
        await validate({ ...formData, ...newData }, currentPanel);
      });
    }
  }

  return (
    <Panel
      {...currentPanel}
      currentStepIndex={currentStepIndex}
      formData={formData}
      loading={loading}
      maxSteps={maxSteps}
      onBack={handleBack}
      onInput={handleInput}
      onNext={handleNext}
      showLeadImage={showLeadImage}
      showProgressBar={formContextValue.showProgressBar}
      validationErrors={validationErrors}
    />
  );
}

export default Form;
